import { DecimalPipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Category, Product } from '@infrab4a/connect';
import { NgxPermissionsService } from 'ngx-permissions';
import { MessageService } from 'primeng/api';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { ErpControllerService } from 'src/app/admin-api';
import {
  DropdownFilter,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import {
  HooklabGroup,
  HooklabService
} from 'src/app/connect-api/api/shop/hooklab.service';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { ShopProductService } from 'src/app/connect-api/api/shop/shop-product.service';
import { NullValue, Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-shop-products',
  templateUrl: './shop-products.component.html',
  styleUrl: './shop-products.component.scss',
  providers: [DecimalPipe]
})
export class ShopProductsComponent implements OnInit {
  @ViewChild(TableComponent) table: TableComponent;
  private title = inject(Title);
  protected productService = inject(ShopProductService);
  protected categoryService = inject(ShopCategoryService);
  private erpService = inject(ErpControllerService);
  private messageService = inject(MessageService);
  private permissionsService = inject(NgxPermissionsService);
  private hooklabService = inject(HooklabService);

  categoriesForProducts: Category[];
  permission = false;
  hooklabGroups: HooklabGroup[] = [];
  cols: TableColumn[];
  millenniumSkuForm = new FormGroup({
    skus: new FormControl<string[]>([], { nonNullable: true })
  });
  chipRegex = /\r|\n|;|,| |\./;
  showMillenniumSkuDialog = false;
  filterValues: { [field: string]: any | Array<any> } = {};
  dropdownFilters: {
    [field: string]: Array<DropdownFilter>;
  } = {
    published: [
      { label: 'Todos', value: NullValue },
      { label: 'Sim', value: true },
      { label: 'Não', value: false }
    ],
    hasVariants: [
      { label: 'Todos', value: NullValue },
      { label: 'Sim', value: true },
      { label: 'Não', value: false }
    ],
    label: [
      { label: 'Todos', value: '' },
      { label: 'Sem label', value: NullValue },
      { label: 'Outlet', value: 'outlet' },
      { label: 'Promoção', value: 'on-sale' },
      { label: 'Últimas unidades', value: 'last-units' },
      { label: 'Glamstar', value: 'glamstar' }
    ],
    categoryId: [{ label: 'Todos', value: '' }],
    validity: [
      { label: 'Todos', value: NullValue },
      { label: 'Sim', value: true },
      { label: 'Não', value: false }
    ],
    group: [{ label: 'Todos', value: NullValue }]
  };

  async ngOnInit() {
    this.title.setTitle('Loja - Produtos');
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue,
      roleAsObject(Role.Portfolio).enumValue
    ]);
    this.cols = this.columns;
    this.categoriesForProducts =
      await this.categoryService.getCategoriesForProduct();
    this.dropdownFilters['categoryId'] = [{ label: 'Todos', value: '' }].concat(
      this.categoriesForProducts.map((cat) => {
        return {
          label: cat.reference,
          value: cat.id
        };
      })
    );
    await this.loadHooklabGroups();
  }

  async loadHooklabGroups(): Promise<void> {
    try {
      const response = await firstValueFrom(this.hooklabService.getGroups());
      this.hooklabGroups = response.data || [];

      this.dropdownFilters['group'] = [
        { label: 'Todos', value: NullValue },
        ...this.hooklabGroups.map((group) => ({
          label: group.name,
          value: group.id
        }))
      ];
    } catch (error) {
      console.error('Erro ao carregar grupos do Hooklab:', error);
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail:
          'Não foi possível carregar os grupos do Hooklab. Usando valores padrão.'
      });
    }
  }

  getGroupName(groupId: number): string {
    if (!groupId) return 'Sem grupo';

    const group = this.hooklabGroups.find((g) => g.id === groupId);
    return group ? group.name : `Grupo ${groupId}`;
  }

  async updatePublished(product: Product) {
    LoaderService.showLoader();
    try {
      await this.productService.updateProduct({
        id: product.id,
        published: product.published
      });
      await this.table.refresh();
      this.messageService.add({
        severity: 'success',
        detail: 'Produto atualizado com sucesso.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async syncMillennium(): Promise<void> {
    try {
      LoaderService.showLoader();
      await lastValueFrom(this.erpService.executeSyncShopProduct());
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Sincronização realizada'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async syncMillenniumBySkus(): Promise<void> {
    const skus = this.millenniumSkuForm.controls.skus.value
      .map((sku) => sku.trim())
      .filter(Boolean);

    if (!skus.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Informe ao menos um SKU para sincronizar.'
      });
      return;
    }

    try {
      LoaderService.showLoader();
      await lastValueFrom(
        this.productService.syncMillenniumProductsBySkus(skus)
      );
      this.millenniumSkuForm.reset({ skus: [] });
      this.showMillenniumSkuDialog = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Sincronização por SKU iniciada.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  get columns() {
    return [
      new TableColumn.Builder()
        .setHeader('ID')
        .setField('id')
        .setRouterLink('product/')
        .setRouterLinkFieldName('id')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Nome')
        .setField('name')
        .setRouterLink('product/')
        .setRouterLinkFieldName('id')
        .setCondition('contains')
        .build(),
      new TableColumn.Builder()
        .setHeader('EAN')
        .setField('EAN')
        .setRouterLink('product/')
        .setRouterLinkFieldName('id')
        .build(),
      new TableColumn.Builder()
        .setHeader('SKU')
        .setField('sku')
        .setRouterLink('product/')
        .setRouterLinkFieldName('id')
        .build(),
      new TableColumn.Builder()
        .setHeader('Tipo')
        .setField('type')
        .setCondition('contains')
        .build(),
      new TableColumn.Builder()
        .setHeader('Marca')
        .setField('brand')
        .setCondition('contains')
        .setRouterLink('/shop-products/categories/category/')
        .setRouterLinkFieldName('category_id')
        .build(),
      new TableColumn.Builder()
        .setHeader('Possui variantes?')
        .setField('hasVariants')
        .setType('boolean')
        .build(),
      new TableColumn.Builder()
        .setHeader('Quantidade')
        .setField('stock')
        .setDisplayFunction(
          (product: Partial<Product>) => product.stock?.quantity || 0
        )
        .setType('formattedInteger')
        .setCondition('gte')
        .build(),
      new TableColumn.Builder()
        .setHeader('Ativo')
        .setField('published')
        .setType(this.permission ? 'inputSwitch' : 'boolean')
        .build(),
      new TableColumn.Builder()
        .setHeader('Label')
        .setField('label')
        .setType('text')
        .setSortable(false)
        .setCondition('equals')
        .setDisplayFunction((product: Partial<Product>) =>
          ShopProductService.getProductLabel(product.label)
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Categoria')
        .setField('categoryId')
        .setType('text')
        .setSortable(false)
        .setRouterLink('/shop-products/categories/category/')
        .setRouterLinkFieldName('categoryId')
        .setDisplayFunction((product: Partial<Product>) => {
          const category = this.categoriesForProducts?.find(
            (cat) => cat.id.toString() === product.category?.id.toString()
          );
          return category?.reference
            ? `${category.reference} ${category.name}`
            : '';
        })
        .build(),
      new TableColumn.Builder()
        .setHeader('Grupo')
        .setField('group')
        .setType('text')
        .setSortable(false)
        .setCondition('equals')
        .setDisplayFunction((product: Partial<Product>) =>
          this.getGroupName(product.group)
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Vigente')
        .setField('validity')
        .setType('boolean')
        .setSortable(false)
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Data criação')
        .setField('createdAt')
        .setType('date')
        .setCondition('between')
        .build(),
      new TableColumn.Builder()
        .setHeader('Últ. atualização')
        .setField('updatedAt')
        .setType('date')
        .setCondition('between')
        .build()
    ];
  }
}
